import { ColDef, RowClickedEvent, ValueFormatterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultSAL3050_R01HeaderDto, SAL3050_R01HeaderDto } from './Dto_SAL3050_R01';
import { useSAL3050_R01Store } from './Store_SAL3050_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'SAL3050_R01-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<SAL3050_R01HeaderDto> | null>;
}

export const Table_SAL3050_R01 = ({ gridRef }: Props) => {
    const {
        SAL3050_R01List,
        selectedSAL3050_R01, setSelectedSAL3050_R01, initSelectedSAL3050_R01,
        columnState, setColumnState,
        scrollTop, setScrollTop,
        setDetailScrollTop,
    } = useSAL3050_R01Store();

    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const {
        getRowId,
        settingRowIndex,
        createRowClassRules,
        paddedGridData,
        ensureSelectedRow,
    } = useGridSettingContent({
        gridRef,
        key: 'customCd',
        gridData: SAL3050_R01List,
        defaultGridData: defaultSAL3050_R01HeaderDto,
        selectedRow: selectedSAL3050_R01,
    });

    const getCellStyle = useCallback((params: { data?: SAL3050_R01HeaderDto }, key: string) => onlyTouchGridStyle(key, 'customNm')(params), []);

    const headerNames: Record<string, string> = {
        rowIndex: '',
        customCd: '거래처코드',
        customNm: '거래처명',
        baseAmt: '기초잔액',
        purAmt: '매입액',
        salAmt: '매출액',
        collectAmt: '수금액',
        payAmt: '지불액',
        balance: '잔액',
        remark: '비고',
    };

    const amtFormatter = (params: ValueFormatterParams<SAL3050_R01HeaderDto>) => {
        if (params.node?.rowPinned === 'bottom' && (params.value === 0 || params.value == null)) {
            return '';
        }
        if (params.value === 0) return '-';
        return params.value?.toLocaleString() ?? '';
    };

    const baseColumnDefs: ColDef<SAL3050_R01HeaderDto>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(),
        {
            headerName: '거래처코드', colId: 'customCd', field: 'customCd', width: 110,
            cellStyle: (p) => getCellStyle(p, 'customCd'),
            headerClass: aggridHeaderClass,
        },
        {
            headerName: '거래처명', colId: 'customNm', field: 'customNm', width: 180,
            cellStyle: (p) => getCellStyle(p, 'customNm'),
            headerClass: aggridHeaderClass,
        },
        {
            headerName: '기초잔액', colId: 'baseAmt', field: 'baseAmt', width: 130,
            cellStyle: (p) => getCellStyle(p, 'baseAmt'),
            headerClass: aggridHeaderClass,
            comparator: (a, b) => a - b,
            valueFormatter: amtFormatter,
        },
        {
            headerName: '매입액', colId: 'purAmt', field: 'purAmt', width: 120,
            cellStyle: (p) => getCellStyle(p, 'purAmt'),
            headerClass: aggridHeaderClass,
            comparator: (a, b) => a - b,
            valueFormatter: amtFormatter,
        },
        {
            headerName: '매출액', colId: 'salAmt', field: 'salAmt', width: 120,
            cellStyle: (p) => getCellStyle(p, 'salAmt'),
            headerClass: aggridHeaderClass,
            comparator: (a, b) => a - b,
            valueFormatter: amtFormatter,
        },
        {
            headerName: '수금액', colId: 'collectAmt', field: 'collectAmt', width: 120,
            cellStyle: (p) => getCellStyle(p, 'collectAmt'),
            headerClass: aggridHeaderClass,
            comparator: (a, b) => a - b,
            valueFormatter: amtFormatter,
        },
        {
            headerName: '지불액', colId: 'payAmt', field: 'payAmt', width: 120,
            cellStyle: (p) => getCellStyle(p, 'payAmt'),
            headerClass: aggridHeaderClass,
            comparator: (a, b) => a - b,
            valueFormatter: amtFormatter,
        },
        {
            headerName: '잔액', colId: 'balance', field: 'balance', width: 130,
            cellStyle: (p) => {
                const base = getCellStyle(p, 'balance') ?? {};
                return (p.value ?? 0) < 0 ? { ...base, color: 'red' } : base;
            },
            headerClass: aggridHeaderClass,
            comparator: (a, b) => a - b,
            valueFormatter: amtFormatter,
        },
        {
            headerName: '비고', colId: 'remark', field: 'remark', width: 180,
            cellStyle: (p) => getCellStyle(p, 'remark'),
            headerClass: aggridHeaderClass,
        },
    ], [getCellStyle]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
    , [baseColumnDefs]);

    const columnDefs: ColDef<SAL3050_R01HeaderDto>[] = useMemo(() => {
        if (columnState === null) {
            return baseColumnDefs;
        }
        return GridCustomColumnDefs<SAL3050_R01HeaderDto>({
            baseColumnDefs,
            columnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

    const handleRowClick = useCallback((e: RowClickedEvent<SAL3050_R01HeaderDto>) => {
        if (e.node.rowPinned === 'bottom') return;
        const row = e.data;
        if (!row) return;
        if (selectedSAL3050_R01?.customCd === row.customCd) {
            setSelectedSAL3050_R01(undefined);
        } else {
            setSelectedSAL3050_R01(row);
        }
        setDetailScrollTop(null);
    }, [selectedSAL3050_R01, setDetailScrollTop, setSelectedSAL3050_R01]);

    const pinnedBottomRowData = useMemo(() => [{
        customNm: `${SAL3050_R01List.length}건`,
        baseAmt: SAL3050_R01List.reduce((s, r) => s + (r.baseAmt ?? 0), 0),
        purAmt: SAL3050_R01List.reduce((s, r) => s + (r.purAmt ?? 0), 0),
        salAmt: SAL3050_R01List.reduce((s, r) => s + (r.salAmt ?? 0), 0),
        collectAmt: SAL3050_R01List.reduce((s, r) => s + (r.collectAmt ?? 0), 0),
        payAmt: SAL3050_R01List.reduce((s, r) => s + (r.payAmt ?? 0), 0),
        balance: SAL3050_R01List.reduce((s, r) => s + (r.balance ?? 0), 0),
    }], [SAL3050_R01List]);

    useEffect(() => { ensureSelectedRow(); }, [SAL3050_R01List, selectedSAL3050_R01]);

    return (
        <GridContextWrapper<SAL3050_R01HeaderDto>
        	enableCellCalculation
            gridRef={gridRef}
            paddedGridData={paddedGridData as SAL3050_R01HeaderDto[]}
            initSelected={initSelectedSAL3050_R01}
            defaultRowData={defaultSAL3050_R01HeaderDto}
            baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
            defaultColumnState={defaultColumnState}
            setColumnState={setColumnState}
            localStorageKey={COLUMN_STATE_KEY}
            menuItems={undefined}
        >
            <Grid_Touch<SAL3050_R01HeaderDto>
                ref={gridRef}
                rowData={paddedGridData as SAL3050_R01HeaderDto[]}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={getRowId}
                rowClassRules={createRowClassRules<SAL3050_R01HeaderDto>({
                    idKey: 'customCd',
                    selectedList: selectedSAL3050_R01 ? [selectedSAL3050_R01.customCd] : [],
                    deletedList: [],
                })}
                onRowClicked={handleRowClick}
                onCellKeyDown={undefined}
                pinnedBottomRowData={pinnedBottomRowData}
                setHoveredRowIndex={setHoveredRowIndex}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setColumnState}
                scrollTop={scrollTop}
                setScrollTop={setScrollTop}
            />
        </GridContextWrapper>
    );
};
